import { ReactNode } from 'react'

/**
 * Recursively computes the total text length of a ReactNode, treating
 * strings and arrays of nodes as text content.
 * @param node - The ReactNode to measure.
 * @returns The total character length of the node's text content.
 * @example
 * getTextLengthOfNode('Lorem ipsum') // 11
 */
export function getTextLengthOfNode(node: ReactNode): number {
	if (typeof node === 'string') return node.length
	if (Array.isArray(node))
		return node.reduce((sum: number, child) => sum + getTextLengthOfNode(child), 0)
	return 0
}

/**
 * Truncates a string by the provided number of characters and includes
 * an ellipsis when the string is truncated.
 * @param str - The string to truncate.
 * @param maxLength - The maximum length of the truncated string.
 * @returns The truncated string.
 * @example
 * truncateStringWithEllipsis('Lorem ipsum dolor sit amet', 10) // 'Lorem ipsu...'
 */
export function truncateStringWithEllipsis(str: string, maxLength: number): string {
	if (!str) return str
	if (str.length <= maxLength) return str
	if (maxLength === 0) return ''

	return `${str.slice(0, maxLength)}...`
}

/**
 * Truncates a string by the provided number of characters.
 * @param str - The string to truncate.
 * @param maxLength - The maximum length of the truncated string.
 * @returns The truncated string.
 * @example
 * truncateString('Lorem ipsum dolor sit amet', 10) // 'Lorem ipsu'
 */
export function truncateString(str: string, maxLength: number): string {
	if (!str) return str
	if (str.length <= maxLength) return str

	return str.slice(0, maxLength)
}

/**
 * Convert snake_case to kebab-case
 * @param str - The string to convert.
 * @returns The update string.
 * @example
 * snakeToKebab('use_the_force') // 'use-the-force'
 */
export function snakeToKebab(str: string) {
	if (!str) return str

	return str.replace(/_/g, '-')
}

/**
 * Convert kebab-case to snake_case
 * @param str - The string to convert.
 * @returns The update string.
 * @example
 * kebabToSnake('use-the-force') // 'use_the_force'
 */
export function kebabToSnake(str: string) {
	if (!str) return str

	return str.replace(/-/g, '_')
}

/**
 * Change a kebab-case string to a normal case string.
 * @param str - The string to convert.
 * @returns The update string.
 * @example
 * deKebab('use-the-force') // 'use_the_force'
 */
export function deKebab(str: string) {
	if (!str) return str

	return str.replace(/-/g, ' ')
}

/**
 * Converts a large number to an abbreviated string, handling negative values.
 * @param num - The number to convert
 * @returns The formatted string
 * @example
 * largeNumToString('10100') // '10.1k'
 * largeNumToString('-10100') // '-10.1k'
 */
export function largeNumToString(num: number | string): string {
	function intlFormat(n: number) {
		return new Intl.NumberFormat().format(Math.round(n * 10) / 10)
	}

	const value = Number(num)
	const roundedValue = Math.round(value)
	const absValue = Math.abs(value)
	// No abbreviation/decimal for numbers less than 1,000 after rounding
	if (absValue < 1_000) {
		if (Math.abs(roundedValue) < 1_000) return new Intl.NumberFormat().format(roundedValue)
		return `${value < 0 ? '-' : ''}${intlFormat(Math.abs(roundedValue) / 1_000)}k`
	}

	if (absValue >= 1_000_000_000)
		return `${value < 0 ? '-' : ''}${intlFormat(absValue / 1_000_000_000)}B`
	if (absValue >= 1_000_000) return `${value < 0 ? '-' : ''}${intlFormat(absValue / 1_000_000)}M`
	if (absValue >= 1_000) return `${value < 0 ? '-' : ''}${intlFormat(absValue / 1_000)}k`

	return intlFormat(value)
}

/**
 * Check if a string is a valid JSON string.
 * @param str - The string to check.
 * @returns True if the string is valid JSON, false otherwise.
 * @example
 * isJson('{"key": "value"}') // true
 */
export function isJsonObjOrArr(str: string): boolean {
	try {
		const parsed = JSON.parse(str)
		// Ensure the parsed value is not a plain string
		return (typeof parsed === 'object' && parsed !== null) || Array.isArray(parsed)
	} catch {
		return false
	}
}

/**
 * Converts a number to a currency format string.
 * @param {number | string} num - The number to be converted to currency.
 * @param {string} currency - The ISO 4217 currency code.
 * @param {number} [decimals=2] - The number of decimal places to display.
 * @returns {string} - The formatted currency string.
 * @throws {Error} - If the provided number is invalid.
 * @throws {Error} - If the provided currency code is invalid.
 */
export function numToCurrency(num: number | string, currency: string, decimals = 2): string {
	// Check if the number is a valid number
	const number = Number(num)
	if (Number.isNaN(number)) throw Error('Invalid number provided')

	// Validate currency code
	const validCurrency = typeof currency === 'string' && /^[A-Z]{3}$/.test(currency)
	// if (!validCurrency) throw Error('Invalid currency code provided')
	if (!validCurrency) return num.toString()

	const options: Intl.NumberFormatOptions = {
		style: 'currency',
		currency,
		maximumFractionDigits: decimals,
		minimumFractionDigits: decimals,
	}

	return new Intl.NumberFormat(
		undefined, // use user's locale
		options
	).format(number)
}

/**
Converts a currency code to its symbol using the Intl.NumberFormat API.
 * @param {string} [currency] - The ISO 4217 currency code (e.g., 'USD', 'EUR').
 */
export function getCurrencySymbol(currency?: string | null): string {
	if (typeof currency !== 'string') {
		// eslint-disable-next-line no-console
		console.info(
			'Invalid currency code provided to getCurrencySymbol. Expected a string, but received:',
			currency
		)
		return ''
	}
	try {
		return (
			(0)
				.toLocaleString(undefined, {
					style: 'currency',
					currency,
					minimumFractionDigits: 0,
					maximumFractionDigits: 0,
				})
				// match numerical characters and remove them to get the currency symbol
				.replace(/\d/g, '')
				.trim()
		)
	} catch {
		// eslint-disable-next-line no-console
		console.info(
			'Invalid currency code provided to getCurrencySymbol. Expected a string, but received:',
			currency
		)
		return ''
	}
}

/**
 * Renders an em dash placeholder for empty values.
 * @param {string | number} [value] - The value to display.
 * @returns {string} - The value as a string, or '--' when there is nothing to show.
 */
export function orDash(value?: string | number | null): string {
	if (value === null || value === undefined || value === '') return '--'
	return String(value)
}

/**
 * Converts a number to a percentage string with a configurable number of decimal places.
 * @param {number} num - The number to convert to a percentage.
 * @param {number} [decimalPlaces=2] - The number of decimal places to display.
 * @returns {string} - The formatted percentage string.
 * @throws {Error} - If the input is not a valid number.
 */
export const numToPercentage = (num: number, decimalPlaces = 2): string => {
	if (typeof num !== 'number' || Number.isNaN(num)) {
		throw new Error('Input must be a valid number')
	}
	if (!Number.isInteger(decimalPlaces) || decimalPlaces < 0) {
		throw new Error('decimalPlaces must be a non-negative integer')
	}
	const percentage = parseFloat((num * 100).toFixed(decimalPlaces))
	const formatted = percentage.toLocaleString('en-US', {
		minimumFractionDigits: decimalPlaces,
		maximumFractionDigits: decimalPlaces,
	})

	return `${formatted}%`
}

export type SupportedCurrencyCode = 'USD' | 'CAD' | 'SEK' | 'GBP' | 'EUR' | 'CHF'

const CURRENCY_NAMES: Record<SupportedCurrencyCode, { singular: string; plural: string }> = {
	USD: { singular: 'Dollar', plural: 'Dollars' },
	CAD: { singular: 'Canadian Dollar', plural: 'Canadian Dollars' },
	SEK: { singular: 'Krona', plural: 'Kronor' },
	GBP: { singular: 'Pound', plural: 'Pounds' },
	EUR: { singular: 'Euro', plural: 'Euros' },
	CHF: { singular: 'Franc', plural: 'Francs' },
}

export type CurrencyNameAndSymbol = {
	name: string
	symbol: string
}

/**
 * Converts an ISO 4217 currency code to its colloquial name and symbol.
 * @param {string} [currency] - The ISO 4217 currency code.
 * @param {number} [amount] - If provided, returns the plural form of the name unless amount is 1.
 * @returns {CurrencyNameAndSymbol} - The colloquial currency name and symbol.
 * @example
 * getCurrencyName('USD') // { name: 'Dollar', symbol: '$' }
 * getCurrencyName('USD', 1) // { name: 'Dollar', symbol: '$' }
 * getCurrencyName('USD', 2) // { name: 'Dollars', symbol: '$' }
 */
export function getCurrencyName(currency?: string | null, amount?: number): CurrencyNameAndSymbol {
	const names = CURRENCY_NAMES[currency as SupportedCurrencyCode]
	if (!names) {
		// eslint-disable-next-line no-console
		console.info(
			'Invalid currency code provided to getCurrencyName. Expected one of:',
			Object.keys(CURRENCY_NAMES),
			'but received:',
			currency
		)
		return { name: '', symbol: getCurrencySymbol(currency) }
	}

	return {
		name: amount === undefined || amount === 1 ? names.singular : names.plural,
		symbol: getCurrencySymbol(currency),
	}
}

/**
 * Converts a string from UPPER_SNAKE_CASE to Title Case.
 * Note: does not handle 'minor' words like 'the', 'and', etc. correctly
 * @param {string} str - The string to convert.
 * @returns {string} - The converted string.
 * @example
 * snakeToTitleCase('USE_THE_FORCE') // 'Use The Force'
 */
export function snakeToTitleCase(str: string): string {
	return str
		.trim()
		.replace(/_+/g, '_') // collapse multiple underscores to one
		.replace(/^_+|_+$/g, '') // remove leading/trailing underscores
		.toLowerCase()
		.split('_')
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ')
}
